import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Calendar, MapPin, ArrowRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { RegisterDialog } from "@/components/site/RegisterDialog";
import {
  type EventItem,
  resolveEventImageUrl,
  getEventStatusRange,
  formatEventDateRange,
} from "@/lib/events";

interface EventHeroSliderProps {
  events: EventItem[];
  mounted?: boolean;
  onSelectEvent?: (event: EventItem) => void;
}

function HeroSlide({
  event,
  mounted,
  onSelectEvent,
}: {
  event: EventItem;
  mounted: boolean;
  onSelectEvent?: (event: EventItem) => void;
}) {
  const status = getEventStatusRange(event);
  const imageUrl = resolveEventImageUrl(event.image_url);

  return (
    <section
      className="relative min-h-[100svh] flex items-center justify-center text-center px-4 sm:px-6 pt-32 overflow-hidden"
      style={{
        backgroundImage: `url('${imageUrl}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />

      <div className={`absolute inset-0 overflow-hidden ${mounted ? "animate-fade-in" : "opacity-0"}`}>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary/20 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
        />
      </div>

      <div className="relative z-10 max-w-5xl py-12">
        <div className="flex flex-wrap justify-center gap-2 mb-6 sm:mb-8">
          {Boolean(event.is_featured) && (
            <Badge className="bg-accent text-accent-foreground border-0">
              <Star className="w-3 h-3 mr-1 fill-current" />
              Featured
            </Badge>
          )}
          {event.category && (
            <Badge variant="secondary" className="bg-white/15 text-white border-white/20">
              {event.category}
            </Badge>
          )}
          {status !== "unknown" && (
            <Badge
              // variant="outline"
              // className={
              //   status === "upcoming"
              //     ? "border-emerald-400/60 text-emerald-200 bg-emerald-950/40"
              //     : "border-white/30 text-white/80 bg-white/10"
              // }
            >
              {/* {status === "upcoming" ? "Upcoming" : "Past Eventdd"} */}
            </Badge>
          )}
        </div>

        <h1
          className={`font-serif text-3xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-white leading-tight mb-5 sm:mb-6 ${mounted ? "animate-fade-in-up" : "opacity-0 translate-y-[20px]"}`}
        >
          {event.title}
        </h1>

        {event.subtitle && (
          <p
            className={`text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 italic max-w-3xl mx-auto mb-6 px-2 font-light ${mounted ? "animate-fade-in-up" : "opacity-0 translate-y-[20px]"}`}
            style={{ animationDelay: "0.2s" }}
          >
            &ldquo;{event.subtitle}&rdquo;
          </p>
        )}

        <div
          className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-8 text-white/80 ${mounted ? "animate-fade-in-up" : "opacity-0 translate-y-[20px]"}`}
          style={{ animationDelay: "0.4s" }}
        >
          {event.location && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm">
              <MapPin className="w-4 h-4 text-accent" />
              <span className="text-sm sm:text-base">{event.location}</span>
            </div>
          )}
          {(event.event_date_from || event.event_date) && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm">
              <Calendar className="w-4 h-4 text-accent" />
              <span className="text-sm sm:text-base">{formatEventDateRange(event)}</span>
            </div>
          )}
        </div>

        <div
          className={`flex flex-col sm:flex-row gap-3 justify-center ${mounted ? "animate-fade-in-up" : "opacity-0 translate-y-[20px]"}`}
          style={{ animationDelay: "0.5s" }}
        >
          <RegisterDialog
            triggerLabel="Register Now"
            triggerSize="lg"
            triggerClassName="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-8 sm:px-12 h-14 sm:h-16 text-base sm:text-lg shadow-2xl shadow-accent/20 hover:shadow-accent/40 transition-all hover:scale-105"
            eventId={event.id}
            eventTitle={event.title}
          />
          <Button
            asChild
            size="lg"
            variant="outline"
            className="border-white/40 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 hover:text-white h-14 sm:h-16 px-8 sm:px-12 text-base sm:text-lg transition-all hover:scale-105"
          >
            <Link
              to="/events/$eventId"
              params={{ eventId: String(event.id) }}
              onClick={() => onSelectEvent?.(event)}
            >
              View this event <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>

      <div
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce ${mounted ? "opacity-100" : "opacity-0"}`}
        style={{ animationDelay: "1s" }}
      >
        <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-white/50 rounded-full" />
        </div>
      </div>
    </section>
  );
}

function DefaultHero({ mounted }: { mounted: boolean }) {
  return (
    <section
      className="relative min-h-[100svh] flex items-center justify-center text-center px-4 sm:px-6 pt-32 overflow-hidden"
      style={{
        backgroundImage: "url('/assets/images/banner1.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
      <div className="relative z-10 max-w-5xl py-12">
        <div className="inline-block px-4 py-1.5 rounded-full border border-accent/40 bg-accent/10 backdrop-blur-sm text-accent text-[10px] sm:text-xs uppercase tracking-[0.3em] mb-6">
          Union of Arab Securities Authorities
        </div>
        <h1 className={`font-serif text-3xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-tight mb-5 ${mounted ? "animate-fade-in-up" : "opacity-0"}`}>
          UASA Events Program
        </h1>
        <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-3xl mx-auto font-light">
          Conferences and gatherings across Arab capital markets.
        </p>
      </div>
    </section>
  );
}

export function EventHeroSlider({ events, mounted = true, onSelectEvent }: EventHeroSliderProps) {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();

  useEffect(() => {
    if (!carouselApi || events.length <= 1) return;
    const autoplay = setInterval(() => carouselApi.scrollNext(), 6000);
    return () => clearInterval(autoplay);
  }, [carouselApi, events.length]);

  if (events.length === 0) {
    return <DefaultHero mounted={mounted} />;
  }

  if (events.length === 1) {
    return <HeroSlide event={events[0]} mounted={mounted} onSelectEvent={onSelectEvent} />;
  }

  return (
    <Carousel opts={{ align: "start", loop: true }} setApi={setCarouselApi} className="w-full">
      <CarouselContent className="ml-0">
        {events.map((event) => (
          <CarouselItem key={event.id} className="pl-0 basis-full">
            <HeroSlide event={event} mounted={mounted} onSelectEvent={onSelectEvent} />
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselPrevious className="left-4 md:left-8 top-1/2 -translate-y-1/2 h-10 w-10 border-white/30 bg-black/30 text-white hover:bg-white/20 hover:text-white" />
      <CarouselNext className="right-4 md:right-8 top-1/2 -translate-y-1/2 h-10 w-10 border-white/30 bg-black/30 text-white hover:bg-white/20 hover:text-white" />
    </Carousel>
  );
}
